// src/services/albumExtensionInvoiceService.js
// Service for album extension invoice management (website version)

import supabase from './supabase';

// Platform details (same as event upgrade invoices)
export const PLATFORM_DETAILS = {
  name: 'Invits',
  tradeName: 'Forte Technologies',
  gstin: '22MIYPS5170D1ZN',
  pan: 'MIYPS5170D',
  address: 'Raipur, Chhattisgarh - 492001',
  state: 'Chhattisgarh',
  stateCode: '22',
  email: 'support@invits.co',
  hsnSac: '998315', // Cloud storage services
};

/**
 * Get album extension invoice by ID
 */
export const getAlbumExtensionInvoiceById = async (invoiceId) => {
  try {
    const { data, error } = await supabase
      .from('album_extension_invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();

    if (error) throw error;

    return {
      success: true,
      invoice: data,
    };
  } catch (error) {
    console.error('Error fetching album extension invoice:', error);
    return {
      success: false,
      error: error.message,
      invoice: null,
    };
  }
};

/**
 * Get all album extension invoices for the current user
 */
export const getUserAlbumExtensionInvoices = async (options = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      .from('album_extension_invoices')
      .select('*')
      .eq('user_id', user.id);

    // Apply filters
    if (options.financialYear) {
      query = query.eq('financial_year', options.financialYear);
    }

    if (options.albumId) {
      query = query.eq('album_id', options.albumId);
    }

    // Order by generated date (newest first)
    query = query.order('generated_at', { ascending: false });

    // Limit if specified
    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      success: true,
      invoices: data || [],
      count: data?.length || 0,
    };
  } catch (error) {
    console.error('Error fetching user album extension invoices:', error);
    return {
      success: false,
      error: error.message,
      invoices: [],
      count: 0,
    };
  }
};

/**
 * Get invoice statistics for current user
 */
export const getAlbumExtensionInvoiceStats = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: invoices, error } = await supabase
      .from('album_extension_invoices')
      .select('total_amount, total_gst')
      .eq('user_id', user.id);

    if (error) throw error;

    const stats = {
      totalInvoices: invoices.length,
      totalSpent: invoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
      totalGst: invoices.reduce((sum, inv) => sum + parseFloat(inv.total_gst || 0), 0),
    };

    return {
      success: true,
      stats,
    };
  } catch (error) {
    console.error('Error fetching invoice stats:', error);
    return {
      success: false,
      error: error.message,
      stats: null,
    };
  }
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount) => {
  if (!amount) return '₹0.00';
  return `₹${parseFloat(amount).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Format date for invoice display
 */
export const formatInvoiceDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};
